import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API } from "../lib/api";

const field =
  "w-full bg-[#0A0A0A] border border-[#27272A] rounded-sm px-3 py-2.5 text-sm text-[#F5F5F5] placeholder:text-[#6b6b6b] outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors";
const labelCls = "text-xs uppercase tracking-[0.18em] text-[#A3A3A3] mb-1.5 block";

export const BookingDialog = ({ open, onOpenChange, type }) => {
  const isHeaven = type === "heaven_on_earth";
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email, darling.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, ...form }),
      });
      if (!res.ok) throw new Error();
      toast.success("Your request is received. A Guardian Angel will reach out shortly.");
      setForm({});
      onOpenChange(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#141414] border border-[#27272A] text-[#F5F5F5] max-w-lg"
        data-testid="booking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-light">
            {isHeaven ? "Heaven on Earth" : "Reserve a Special Event"}
          </DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">
            {isHeaven
              ? "A private, premium appointment with one of our Fallen Angel Models."
              : "Let us craft an unforgettable evening for your gathering."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Full Name</label>
              <input className={field} value={form.name || ""} onChange={set("name")} data-testid="booking-name" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input className={field} type="email" value={form.email || ""} onChange={set("email")} data-testid="booking-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Phone</label>
              <input className={field} value={form.phone || ""} onChange={set("phone")} data-testid="booking-phone" />
            </div>
            <div>
              <label className={labelCls}>{isHeaven ? "Model Preference" : "Occasion"}</label>
              <input className={field} value={form.model_preference || ""} onChange={set("model_preference")} data-testid="booking-preference" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Preferred Date</label>
              <input className={field} type="date" value={form.date || ""} onChange={set("date")} data-testid="booking-date" />
            </div>
            <div>
              <label className={labelCls}>Preferred Time</label>
              <input className={field} type="time" value={form.time || ""} onChange={set("time")} data-testid="booking-time" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea className={field} rows={3} value={form.notes || ""} onChange={set("notes")} data-testid="booking-notes" />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-[#0A0A0A] font-semibold py-3 rounded-sm hover:bg-[#F3E5AB] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="booking-submit"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Request Appointment
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
